import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Routes where chatbot should be HIDDEN (public/auth routes)
const CHATBOT_HIDDEN_ROUTES = [
  '/log-in',
  '/sign-up',
  '/new-sign-up',
  '/forgot-password',
  '/reset-password',
  '/create-account',
  '/request-token',
  '/verify-email',
  '/set-password',
];

/**
 * ChatbotVisibilityManager
 *
 * Manages the visibility of the Dante AI support chatbot based on the current route.
 * Hides the chatbot on public/authentication pages and shows it on private pages.
 *
 * The chatbot is loaded via bubble-embed.js script in index.html which creates:
 * - A fixed position bubble button (icon wrapper)
 * - A chat iframe container
 * - Tooltip elements
 *
 * This component finds these elements and toggles their display based on route.
 */
const ChatbotVisibilityManager = () => {
  const location = useLocation();
  const hideTimeoutRef = useRef(null);
  const initCheckIntervalRef = useRef(null);

  // Helper: Check if current path should hide chatbot
  const shouldHideChatbot = (path) => {
    return CHATBOT_HIDDEN_ROUTES.some((route) => path.startsWith(route));
  };

  // Helper: Find chatbot elements in DOM
  const getChatbotElements = () => {
    // The bubble-embed script creates elements with specific characteristics:
    // - Fixed position with high z-index (99999, 999990, or 999999)
    // - Positioned at bottom-right corner
    // - Includes bubble icon, chat container, and tooltip

    const elements = [];

    // Find all fixed position elements
    const fixedElements = document.querySelectorAll('[style*="position: fixed"], [style*="position:fixed"]');

    fixedElements.forEach((el) => {
      const style = el.getAttribute('style') || '';
      const computedStyle = window.getComputedStyle(el);
      const zIndex = computedStyle.zIndex;

      // Chatbot elements have z-index 99999+ and are positioned bottom-right
      if (
        (zIndex === '99999' || zIndex === '999990' || zIndex === '999999') &&
        (style.includes('bottom') || computedStyle.bottom !== 'auto') &&
        (style.includes('right') || computedStyle.right !== 'auto')
      ) {
        elements.push(el);
      }
    });

    return elements;
  };

  // Helper: Hide chatbot elements
  const hideChatbot = () => {
    const elements = getChatbotElements();

    elements.forEach((el) => {
      // Store original display value if not already stored
      if (!el.dataset.originalDisplay) {
        const currentDisplay = window.getComputedStyle(el).display;
        el.dataset.originalDisplay = currentDisplay;
      }

      // Hide the element
      el.style.setProperty('display', 'none', 'important');
    });

  };

  // Helper: Show chatbot elements
  const showChatbot = () => {
    const elements = getChatbotElements();

    elements.forEach((el) => {
      // Restore original display value or remove the override
      if (el.dataset.originalDisplay) {
        el.style.setProperty('display', el.dataset.originalDisplay, 'important');
      } else {
        el.style.removeProperty('display');
      }
    });

  };

  // Effect: Manage chatbot visibility on route change
  useEffect(() => {
    const currentPath = location.pathname;

    // Clear any pending operations
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    if (initCheckIntervalRef.current) {
      clearInterval(initCheckIntervalRef.current);
    }

    if (shouldHideChatbot(currentPath)) {
      // On public routes: hide chatbot immediately and keep checking
      // (in case script hasn't loaded yet)
      hideChatbot();

      // Keep checking for a few seconds in case script loads late
      let checks = 0;
      const maxChecks = 10;

      initCheckIntervalRef.current = setInterval(() => {
        checks++;
        hideChatbot();

        if (checks >= maxChecks) {
          clearInterval(initCheckIntervalRef.current);
        }
      }, 500);

    } else {
      // On private routes: show chatbot
      // Add small delay to let page render first
      hideTimeoutRef.current = setTimeout(() => {
        showChatbot();
      }, 100);
    }

    // Cleanup
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (initCheckIntervalRef.current) {
        clearInterval(initCheckIntervalRef.current);
      }
    };
  }, [location.pathname]);

  // This component doesn't render anything
  return null;
};

export default ChatbotVisibilityManager;
